import { useSelector } from 'react-redux';
import { formatChangeDisplay, getChangeColor } from '../utils/chartHelpers';
import { formatNumber } from '../../../utils/formatters';

const tooltipBaseStyle = {
  background: 'white',
  borderRadius: '8px',
  color: '#1F2937',
  fontSize: '13px',
  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
  border: '1px solid #E5E7EB',
};

const ComparisonTooltip = ({ data, populationHealth }) => (
  <div style={{ ...tooltipBaseStyle, padding: '16px', minWidth: '240px' }}>
    <div style={{
      fontWeight: 600,
      fontSize: '15px',
      marginBottom: '12px',
      color: '#111827'
    }}>
      {data.fullName}
    </div>

    {/* Baseline data */}
    <div style={{
      marginBottom: '8px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}>
      <span style={{ color: '#6B7280', display: 'flex', alignItems: 'center' }}>
        <div style={{
          width: '8px',
          height: '8px',
          backgroundColor: data.color,
          opacity: 0.6,
          borderRadius: '2px',
          marginRight: '6px',
        }} />
        {populationHealth?.baseline || 'Baseline'}:
      </span>
      <span style={{ fontWeight: 600, color: '#1F2937' }}>
        {formatNumber(data.baseline)} patients ({data.baselinePercent}%)
      </span>
    </div>

    {/* Follow-up data */}
    <div style={{
      marginBottom: '8px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}>
      <span style={{ color: '#6B7280', display: 'flex', alignItems: 'center', marginRight: '16px', }}>
        <div style={{
          width: '8px',
          height: '8px',
          backgroundColor: data.color,
          borderRadius: '2px',
          marginRight: '6px',
        }} />
        {populationHealth?.followup || 'Follow-up'}:
      </span>
      <span style={{ fontWeight: 600, color: '#1F2937' }}>
        {formatNumber(data.followup)} patients ({data.followupPercent}%)
      </span>
    </div>

    {/* Change indicator */}
    <div style={{
      marginBottom: '8px',
      padding: '6px 8px',
      backgroundColor: '#F8FAFC',
      borderRadius: '4px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}>
      <span style={{ color: '#6B7280', fontSize: '12px' }}>
        {populationHealth?.change || 'Change'}:
      </span>
      <span style={{
        fontWeight: 600,
        color: getChangeColor(data.followup, data.baseline),
        fontSize: '12px',
      }}>
        {formatChangeDisplay(data.followup, data.baseline)}
      </span>
    </div>

    <div style={{
      fontSize: '11px',
      color: '#9CA3AF',
      marginTop: '8px',
      paddingTop: '8px',
      borderTop: '1px solid #F3F4F6',
      textAlign: 'center',
    }}>
      {populationHealth?.clickToViewDetails || 'Click to view patient details'}
    </div>
  </div>
);

const SingleModeTooltip = ({ data, populationHealth }) => (
  <div style={{ ...tooltipBaseStyle, padding: '16px', minWidth: '200px' }}>
    <div style={{
      fontWeight: 600,
      fontSize: '15px',
      marginBottom: '12px',
      color: '#111827'
    }}>
      {data.fullName}
    </div>

    <div style={{
      marginBottom: '8px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}>
      <span style={{ color: '#6B7280' }}>
        {populationHealth?.patients || 'Patients'}:
      </span>
      <span style={{ fontWeight: 600, color: '#1F2937' }}>
        {formatNumber(data.value)} ({data.percent}%)
      </span>
    </div>

    <div style={{
      fontSize: '11px',
      color: '#9CA3AF',
      marginTop: '8px',
      paddingTop: '8px',
      borderTop: '1px solid #F3F4F6',
      textAlign: 'center',
    }}>
      {populationHealth?.clickToViewDetails || 'Click to view patient details'}
    </div>
  </div>
);

const ChartTooltip = ({ active, payload, isComparisonMode }) => {
  const { en: { populationHealth } } = useSelector((state) => state.i18n);

  if (!active || !payload || !payload.length) {
    return null;
  }

  const data = payload[0].payload;

  return isComparisonMode ? (
    <ComparisonTooltip data={data} populationHealth={populationHealth} />
  ) : (
    <SingleModeTooltip data={data} populationHealth={populationHealth} />
  );
};

export default ChartTooltip;
